import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyUnicefMode({ brand, today, Copy }: ModeRendererProps) {
  // UNICEF gradient: child wellbeing spectrum
  const unicefGradient = (
    <defs>
      <linearGradient id="unicefGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00AEEF" />
        <stop offset="50%" stopColor="#FFC72C" />
        <stop offset="100%" stopColor="#FF5722" />
      </linearGradient>
      <radialGradient id="safeGlow" cx="50%" cy="50%">
        <stop offset="0%" stopColor="#FFC72C" stopOpacity={0.5} />
        <stop offset="100%" stopColor="#00AEEF" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Toy tessellations: playful geometry
  const toyTessellations: JSX.Element[] = [];
  const shapes = ["circle", "square", "triangle"];
  for (let row = 0; row < 14; row++) {
    for (let col = 0; col < 10; col++) {
      const x = 100 + col * 60;
      const y = 120 + row * 65;
      const shape = shapes[(row + col) % 3];
      const size = 16 + Math.sin(row * col * 0.1) * 6;
      
      if (shape === "circle") {
        toyTessellations.push(
          <circle
            key={`toy-${row}-${col}`}
            cx={x}
            cy={y}
            r={size}
            fill="url(#unicefGrad)"
            opacity={0.4}
          />
        );
      } else if (shape === "square") {
        toyTessellations.push(
          <rect
            key={`toy-${row}-${col}`}
            x={x - size}
            y={y - size}
            width={size * 2}
            height={size * 2}
            fill="url(#unicefGrad)"
            opacity={0.4}
            rx={4}
          />
        );
      } else {
        const h = size * 1.5;
        toyTessellations.push(
          <polygon
            key={`toy-${row}-${col}`}
            points={`${x},${y - h} ${x + size},${y + h / 2} ${x - size},${y + h / 2}`}
            fill="url(#unicefGrad)"
            opacity={0.4}
          />
        );
      }
    }
  }

  // Learning paths: education trajectories
  const learningPaths: JSX.Element[] = [];
  for (let i = 0; i < 8; i++) {
    const points = [];
    for (let j = 0; j < 24; j++) {
      const angle = (j * Math.PI * 2) / 24 + i * 0.3;
      const radius = 120 + j * 6;
      points.push(`${397 + radius * Math.cos(angle)},${561.5 + radius * Math.sin(angle)}`);
    }
    learningPaths.push(
      <polyline
        key={`path-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#unicefGrad)"
        strokeWidth={2}
        opacity={0.3}
      />
    );
  }

  // Safe-zone halos: child protection
  const safeZoneHalos: JSX.Element[] = [];
  const zones = [100, 150, 200, 250];
  zones.forEach((radius, i) => {
    safeZoneHalos.push(
      <circle
        key={`zone-${i}`}
        cx={397}
        cy={561.5}
        r={radius}
        fill="none"
        stroke="#00AEEF"
        strokeWidth={3}
        opacity={0.4 - i * 0.08}
        strokeDasharray="12,8"
      />
    );
  });

  // Wellbeing indicators
  const wellbeingIndicators: JSX.Element[] = [];
  const indicators = 12;
  for (let i = 0; i < indicators; i++) {
    const angle = (i * Math.PI * 2) / indicators;
    const radius = 220;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    wellbeingIndicators.push(
      <g key={`indicator-${i}`}>
        <circle cx={x} cy={y} r={20} fill="url(#safeGlow)" />
        <circle cx={x} cy={y} r={14} fill="none" stroke="url(#unicefGrad)" strokeWidth={2} />
        {/* Smiley face */}
        <circle cx={x - 4} cy={y - 2} r={2} fill="#FFFFFF" />
        <circle cx={x + 4} cy={y - 2} r={2} fill="#FFFFFF" />
        <path
          d={`M ${x - 6},${y + 4} Q ${x},${y + 8} ${x + 6},${y + 4}`}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={1.5}
        />
      </g>
    );
  }

  // Central UNICEF core
  const unicefCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={90} fill="url(#safeGlow)" />
      <circle cx={397} cy={561.5} r={65} fill="none" stroke="#FFC72C" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={45} fill="none" stroke="#00AEEF" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={25} fill="none" stroke="#FF5722" strokeWidth={2} />
      {/* Mother and child silhouette */}
      <circle cx={387} cy={551.5} r={12} fill="#FFFFFF" opacity={0.8} />
      <circle cx={407} cy={561.5} r={8} fill="#FFFFFF" opacity={0.8} />
    </g>
  );

  return (
    <g>
      {unicefGradient}
      {toyTessellations}
      {safeZoneHalos}
      {learningPaths}
      {wellbeingIndicators}
      {unicefCore}
      {/* Render Copy object safely (Copy is an object from constants, not a React component) */}
      {Copy && typeof Copy === "object" && (
        <g fontFamily="monospace" fontSize={9} fill="#FFFFFF" opacity={0.9}>
          <text x={397} y={1040} textAnchor="middle" fontWeight={700}>
            {Copy.title}
          </text>
          <text x={397} y={1056} textAnchor="middle">
            {Copy.subtitle}
          </text>
                <text x={397} y={1072} textAnchor="middle">
                  {/* date/version removed */}
          </text>
        </g>
      )}
    </g>
  );
}
